import {
    CodeEditor,
    CodeEditorLanguage,
    Input,
    Label,
    Message,
    MessageStatus
} from "@app/form_components";
import { VersionSelector } from "@app/page-components";
import { ItemListView } from "@app/types";
import { createUuid, stringify } from "@app/utilities";
import BaseDialog from "./BaseDialog";
import OldItemList from "../../form_components/OldItemList";
import map from "lodash/map";
import { isEqual } from "lodash";

/**
 * JSDoc imports.
 *
 * @import { DesignFunction } from "@app/types";
 */

export default class CustomFunctionDialog extends BaseDialog {
    /**
     * @param {Object} props
     * @param {DesignFunction} [props.customFunction] Design fuction to modify (leave blank for new).
     * @param {(customFunction: DesignFunction) => void} props.onSave - Callback to save the function
     * @param {() => void} [props.onDelete] - Callback to delete the function (for editing)
     */
    constructor(props) {
        super(props);
        this.onAdd = this.onAdd.bind(this);
        this.onDelete = this.onDelete.bind(this);
        const {
            name,
            parameters,
            script
        } = props.customFunction ?? {};
        this.state = {
            compareSerialized: null,
            name,
            parameters,
            script
        };
    }

    /**
     * Add to application
     * @param event
     */
    onAdd() {
        const { customFunction = {} } = this.props;
        const config = {
            name: this.state.name,
            parameters: this.state.parameters,
            script: this.state.script
        };

        const timeStamps = {
            createdTime: customFunction.createdTime ?? window.apm.timestamp,
            id: customFunction.id ?? createUuid(),
            lastLocalChange: window.apm.timestamp,
            lastSavedChange: customFunction.lastSavedChange
        };

        if (!customFunction.id) {
            this.props.onSave({ ...config, ...timeStamps });
            w2popup.close();
            return;
        }

        const oldConfig = {
            name: customFunction.name,
            parameters: customFunction.parameters,
            script: customFunction.script
        };

        if (isEqual(config, oldConfig)) {
            return;
        }

        this.props.onSave({ ...config, ...timeStamps });
        w2popup.close();
    }

    /**
     * Remove from application
     * @param event
     */
    onDelete() {
        this.props.onDelete?.();
        w2popup.close();
    }

    /**
     * Set config proxy
     * @param {string} key
     * @param {string|Object} value
     */
    setConfig(key, value) {
        this.setState({ [key]: value });
    }

    componentDidMount() {
        super.componentDidMount();
        document.addEventListener("ev-dialog-add", this.onAdd);
        document.addEventListener("ev-dialog-delete", this.onDelete);
        document.addEventListener("ev-item-list-update", this.updateHandle);
    }

    unregister() {
        document.removeEventListener("ev-dialog-add", this.onAdd);
        document.removeEventListener("ev-dialog-delete", this.onDelete);
        document.removeEventListener("ev-item-list-update", this.updateHandle);
    }

    /**
     * Get function "argument" part for display
     * @returns {string}
     */
    getScriptHeader() {
        var serializedParams = "";
        const params = map(this.state.parameters ?? [], p => {
            return p.key + (p.value ? " = " + p.value : "");
        });
        if (params.length) {
            serializedParams = params.join(", ");
        }
        return "function (" + serializedParams + ") {";
    }

    /**
     * Get function "closure" part for display
     * @returns {string}
     */
    getScriptFooter() {
        return "}";
    }

    /**
     * React render
     * @returns {XML}
     */
    render() {
        const { serialized } = window.apm;
        const { compareSerialized } = this.state;
        const compareFunction = compareSerialized?.functions.find(({ id }) =>
            id === this.props.customFunction.id
        );

        return (
            <div className="modal-inner flex-column flex-gap" style={{ minHeight: '100%' }}>
                <Input
                    label={window.l.popup.function_name}
                    name="functionName"
                    onChange={(name) => this.setState({ name })}
                    required
                    value={this.state.name ?? "Untitled function"}
                />
                <OldItemList
                    label={window.l.popup.parameters}
                    view={ItemListView.keyValue}
                    context={this}
                    valKey={"parameters"}
                    value={this.state.parameters ?? []}
                    pairKeyView={{ type: "Input", key: "key", hint: window.l.popup.parameter_name }}
                    pairValueView={{ type: "Input", key: "value", hint: window.l.popup.default_value }}
                    icon={"arrow-left"}
                    allowDelete={true}
                    broadcast={true}
                />
                <VersionSelector
                    branchId={serialized.branchId}
                    fieldConfig={{ className: "z-over-code-editor" }}
                    isClearable
                    label="Compare Against:"
                    name="versionSelector"
                    onChange={(_, option) => {
                        this.setState({ compareSerialized: option?.serialized });
                        const isExpanded = parseInt(document.querySelector('.w2ui-popup').style.top) < 20;
                        if (!isExpanded) {
                            document.querySelector('.w2ui-popup-max').click();
                        }
                    }}
                    value={compareSerialized?.version ?? null}
                />
                {(!compareSerialized || compareFunction) ? null : (
                    <Message
                        message="Unable to find matching function in selected version."
                        status={MessageStatus.error}
                    />
                )}
                {(!compareFunction) ? null : (
                    <>
                        <Label>Compare {window.l.popup.parameters}</Label>
                        <div className="script-editor-container">
                            <CodeEditor
                                originalValue={stringify(compareFunction.parameters ?? [])}
                                readOnly
                                language={CodeEditorLanguage.json}
                                value={stringify(this.state.parameters ?? [])}
                            />
                        </div>
                    </>
                )}
                <div className="bold">{this.getScriptHeader()}</div>
                <div className="script-editor-container">
                    <CodeEditor
                        header={this.getScriptHeader()}
                        footer={this.getScriptFooter()}
                        onChange={(value) => this.setState({ script: value })}
                        originalValue={compareFunction?.script}
                        value={this.state.script ?? ""}
                    />
                </div>
                <div className="bold">
                    {this.getScriptFooter()}
                </div>
            </div>
        );
    }
}